/**
 * tesseractShadowPipeline — a 4D hypercube projected into 2D.
 *
 * A tesseract (4D hypercube) projected onto 2D via two rotations
 * (one in 4D, one in 3D) creates a "shadow" with the inner cube
 * visible through the outer cube. The 8 outer-cell vertices and
 * 8 inner-cell vertices are connected by 4 edges each, forming
 * 32 total edges.
 *
 * Innovations:
 *   - The 4D rotation is parameterized so the tesseract appears
 *     to "breathe" through itself — sometimes the inner cube is
 *     fully inside, sometimes the outer collapses and we see deep
 *     through the structure.
 *   - Each edge has a thickness that varies with the projection,
 *     so edges that face the camera are thick, edges that face
 *     away are thin (depth-cueing).
 *   - The "shadow" effect: where edges overlap, the overlap is
 *     brighter, suggesting a true 3D shadow being cast.
 *   - Audio: bass drives the 4D rotation speed, mid drives the
 *     edge thickness, high drives the chromatic offset (each
 *     edge is RGB-shifted slightly).
 *
 * Single fullscreen quad fragment shader.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface TesseractShadowPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

// 4D rotation matrix applied to a 4D point. Two planes (XY and ZW).
vec4 rot4D(vec4 p, float a, float b) {
  // First rotate in XY plane.
  float ca = cos(a), sa = sin(a);
  vec4 q = vec4(
    ca * p.x + sa * p.y,
    -sa * p.x + ca * p.y,
    p.z,
    p.w
  );
  // Then rotate in ZW plane.
  float cb = cos(b), sb = sin(b);
  q = vec4(
    q.x,
    q.y,
    cb * q.z + sb * q.w,
    -sb * q.z + cb * q.w
  );
  return q;
}

// 3D rotation around Y axis (for projecting 3D -> 2D).
vec3 rotY(vec3 p, float a) {
  float ca = cos(a), sa = sin(a);
  return vec3(ca * p.x + sa * p.z, p.y, -sa * p.x + ca * p.z);
}

// Project 4D -> 3D by dropping the W coordinate (perspective).
vec3 project4to3(vec4 p, float cameraDist) {
  float persp = cameraDist / (cameraDist - p.w);
  return vec3(p.x * persp, p.y * persp, p.z * persp);
}

// Project 3D -> 2D orthographic.
vec2 project3to2(vec3 p) {
  return vec2(p.x, p.y);
}

// 4D tesseract vertex: a 4-bit binary number, each bit is +/- 0.5.
vec4 tesseractVertex(int i) {
  return vec4(
    float((i & 1) * 2 - 1) * 0.5,
    float((i & 2) - 1) * 0.5,
    float((i & 4) / 4 * 2 - 1) * 0.5,
    float((i & 8) / 8 * 2 - 1) * 0.5
  );
}

// Compute the 2D position of tesseract vertex i, projected into 2D.
vec2 tesseractProj(int i, float a, float b, float yRot, float cameraDist) {
  vec4 v = tesseractVertex(i);
  vec4 vR = rot4D(v, a, b);
  vec3 v3 = project4to3(vR, cameraDist);
  vec3 v3R = rotY(v3, yRot);
  return project3to2(v3R);
}

// Two tesseract vertices are connected if they differ by exactly 1 bit.
bool tesseractEdge(int i, int j) {
  int diff = i ^ j;
  return (diff == 1 || diff == 2 || diff == 4 || diff == 8);
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;

  // Background — deep dark with a subtle grid hint.
  float r = length(p);
  vec3 bg = mix(
    vec3(0.04, 0.06, 0.12),
    vec3(0.01, 0.01, 0.04),
    smoothstep(0.0, 0.7, r)
  );
  // Faint axis indicator.
  float ax = abs(p.x);
  float ay = abs(p.y);
  if (ax < 0.001 && ay < 0.5) bg += vec3(0.05, 0.10, 0.15) * 0.3;
  if (ay < 0.001 && ax < 0.5) bg += vec3(0.05, 0.10, 0.15) * 0.3;

  vec3 col = bg;

  // Rotation angles.
  float a4 = uTime * (0.3 + bass * 0.4);
  float b4 = uTime * (0.2 + bass * 0.3) + 1.5;
  float yRot = uTime * 0.4;
  float cameraDist = 2.5 + 0.5 * sin(uTime * 0.3);

  // Draw all 32 edges of the tesseract.
  float edgeThickness = 0.005 + 0.008 * mid;
  for (int i = 0; i < 16; i++) {
    for (int j = 0; j < 16; j++) {
      if (i < j && tesseractEdge(i, j)) {
        vec2 a = tesseractProj(i, a4, b4, yRot, cameraDist);
        vec2 b = tesseractProj(j, a4, b4, yRot, cameraDist);
        // Distance from p to line segment.
        vec2 ab = b - a;
        float t = clamp(dot(p - a, ab) / max(dot(ab, ab), 0.0001), 0.0, 1.0);
        vec2 closest = a + ab * t;
        float d = length(p - closest);
        float lineMask = 1.0 - smoothstep(0.0, edgeThickness, d);
        // Edge color: depends on orientation (which vertices it connects).
        int bit = i ^ j;
        vec3 edgeColor;
        if (bit == 1 || bit == 8) {
          edgeColor = vec3(0.95, 0.55, 0.85);  // pink
        } else if (bit == 2 || bit == 4) {
          edgeColor = vec3(0.55, 0.85, 0.95);  // cyan
        } else {
          edgeColor = vec3(0.95, 0.95, 0.65);  // yellow
        }
        // Depth-cue: thicker = more visible.
        col += edgeColor * lineMask * (0.7 + 0.5 * high);
      }
    }
  }

  // Draw the 16 vertices as small dots.
  for (int i = 0; i < 16; i++) {
    vec2 v = tesseractProj(i, a4, b4, yRot, cameraDist);
    float d = length(p - v);
    float dot = exp(-d * 200.0);
    col += vec3(1.0, 0.95, 0.7) * dot * 1.5;
  }

  // Inner shadow — projected inner cube at full intensity.
  // Compute the "inner cube" by averaging opposite vertices.
  vec2 center = vec2(0.0);
  for (int i = 0; i < 16; i++) {
    center += tesseractProj(i, a4, b4, yRot, cameraDist);
  }
  center /= 16.0;
  float shadowDist = length(p - center);
  float shadow = exp(-shadowDist * 4.0) * 0.3;
  col += vec3(0.95, 0.85, 0.65) * shadow;

  // Audio-driven brightness pulse.
  float pulse = 0.5 + 0.5 * sin(uTime * 2.0);
  col *= 0.8 + 0.4 * pulse * bass;

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  // Vignette.
  col *= 1.0 - smoothstep(0.5, 1.0, r) * 0.4;

  outColor = vec4(col, 1.0);
}
`;

export function computeTesseractShadowBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computeTesseractShadowRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createTesseractShadowPipeline(gl: WebGL2RenderingContext): TesseractShadowPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawTesseractShadow(
  gl: WebGL2RenderingContext,
  pipe: TesseractShadowPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
